export const GAME_WIDTH = 960;
export const GAME_HEIGHT = 720;

export interface ArenaBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const ARENA_BOUNDS: ArenaBounds = {
  x: 72,
  y: 112,
  width: 816,
  height: 536,
};

export const BASE_BALL_COUNT = 3;
export const BALL_RADIUS = 30;
export const LEVEL_DURATION_MS = 30_000;
export const SCORE_POINTS_PER_SECOND = 10;

export const PHYSICS = {
  fixedStepMs: 1000 / 120, // Simulation timestep; smaller steps make motion more stable.
  forceStrength: 999_000_0, // Charge attraction/repulsion strength; higher values make balls accelerate faster.
  softening: 120, // Prevents huge force spikes when balls get very close together.
  damping: 0.5, // Motion drag; lower values let balls keep more speed.
  bounce: 0.94, // Wall rebound energy; higher values make wall hits preserve more speed.
  maxSpeed: 140, // Hard speed cap after forces are applied.
} as const;

export type OrbitSwirl = "cw" | "ccw";
export type WallColor = "red" | "blue" | "gray";

export interface AuthoredLevelParameters {
  durationMs: number;
  forceStrength: number;
  bounce: number;
  radiusX: number;
  radiusY: number;
  baseSpeed: number;
  rotationDeg: number;
  swirl: OrbitSwirl;
  leftWallColor: WallColor;
  rightWallColor: WallColor;
  topWallColor: WallColor;
  bottomWallColor: WallColor;
}

export const DEFAULT_LEVEL_PARAMETERS: AuthoredLevelParameters = {
  durationMs: LEVEL_DURATION_MS, // Default survival timer for each level.
  forceStrength: PHYSICS.forceStrength, // Default attraction/repulsion strength for authored levels.
  bounce: PHYSICS.bounce, // Default wall rebound energy for authored levels.
  radiusX: 270, // Horizontal spawn spread for authored orbit layouts.
  radiusY: 220, // Vertical spawn spread for authored orbit layouts.
  baseSpeed: 32, // Starting tangential speed before per-ball scaling is applied.
  rotationDeg: 0, // Whole-layout rotation around the arena center.
  swirl: "cw", // Default orbit direction for authored layouts.
  leftWallColor: "gray", // Left wall color for levels that do not override it.
  rightWallColor: "gray", // Right wall color for levels that do not override it.
  topWallColor: "red", // Top wall color for levels that do not override it.
  bottomWallColor: "blue", // Bottom wall color for levels that do not override it.
};

export const COLORS = {
  backgroundTop: 0x07131d,
  backgroundBottom: 0x11293b,
  backgroundGlow: 0x204763,
  arenaFill: 0x0d2130,
  arenaEdge: 0x7dcfff,
  arenaInnerEdge: 0x19364a,
  textPrimary: "#eef7ff",
  textMuted: "#9cb7ca",
  positiveBall: 0xff6f61,
  positiveHighlight: 0xffd8cc,
  negativeBall: 0x5cb7ff,
  negativeHighlight: 0xd4f0ff,
  wallRed: 0xff6f61,
  wallBlue: 0x5cb7ff,
  wallGray: 0x9aa7b4,
  warning: "#ffd27d",
  success: "#b5ffce",
  failure: "#ffc0b7",
} as const;

export function chargeToFillColor(charge: number): number {
  return charge > 0 ? COLORS.positiveBall : COLORS.negativeBall;
}

export function chargeToHighlightColor(charge: number): number {
  return charge > 0 ? COLORS.positiveHighlight : COLORS.negativeHighlight;
}

export function chargeToTextColor(charge: number): string {
  return charge > 0 ? "#ff6f61" : "#5cb7ff";
}

export function wallColorToFillColor(color: WallColor): number {
  switch (color) {
    case "red":
      return COLORS.wallRed;
    case "blue":
      return COLORS.wallBlue;
    case "gray":
      return COLORS.wallGray;
  }
}
